import type {
	FeatureTimelineRebuildExecutorDependencies,
	FeatureTimelineRebuildInput,
	FeatureTimelineRebuildResult,
} from '../../../types/feature/FeatureTimelineRebuild';
import {featureCommandAdapterRegistry} from '../command/FeatureCommandAdapterRegistry';
import {
	resolveTopologySourceOwnerIdentityKey,
	type TopologySourceOwnerDescriptor,
} from '../../reference/sketch-entity-reference/model/ExternalGeometrySourceDescriptor';
import {FeatureRollbackTimelineItemKind} from '../rollback/FeatureRollbackTypes';
import {isStaleWorkerResponseError} from '../../worker/StaleWorkerResponseError';

/**
 * 阶段 B 的唯一时间线前向重建执行器。
 *
 * 本类只负责不可变计划、Worker 分段推进和 Convert Entities checkpoint；
 * 队列、完整历史与 Viewer 投影仍由现有宿主管理。
 */
export class FeatureTimelineRebuildExecutor {
	/**
	 * @param dependencies Worker、Convert Entities 与输入有效性依赖。
	 */
	public constructor(
		private readonly dependencies: FeatureTimelineRebuildExecutorDependencies,
	) {}

	/**
	 * 执行一次时间线前向重建。
	 *
	 * 对外只有一个执行入口，避免调用方自行交错 Worker 分段与 checkpoint。
	 *
	 * @param input 同一同步边界冻结的时间线、引用与 revision。
	 * @returns 成功前缀、正式完整历史和诊断。
	 */
	public async execute(
		input: FeatureTimelineRebuildInput,
	): Promise<FeatureTimelineRebuildResult> {
		const fullCommands = input.fullCommands.map((command) => structuredClone(command));
		const evaluationCommands = input.evaluationCommands.map((command) => (
			structuredClone(command)
		));
		const activeCommands: CadCommand[] = [];
		const nextFullCommandById = new Map(
			fullCommands.map((command) => [command.id, structuredClone(command)]),
		);
		const warnings: FeatureTimelineRebuildResult['warnings'] = [];
		const changedSketchIds = new Set<string>();
		const changedReferenceIds = new Set<string>();
		let segmentCount = 0;
		let checkpointQueryCount = 0;
		let latestSummary: WorkerGeometryStateSummary | undefined;
		let ownedWorkerRevision = input.initialWorkerRevision;
		let hasExecutedSegment = false;
		const createResult = (
			status: FeatureTimelineRebuildResult['status'],
			overrides: Partial<FeatureTimelineRebuildResult> = {},
		): FeatureTimelineRebuildResult => ({
			status,
			summary: latestSummary,
			activeCommands: activeCommands.map((command) => structuredClone(command)),
			fullCommands: fullCommands.map((command) => (
				structuredClone(nextFullCommandById.get(command.id) ?? command)
			)),
			warnings: [...warnings],
			changedSketchIds: [...changedSketchIds],
			changedReferenceIds: [...changedReferenceIds],
			segmentCount,
			checkpointQueryCount,
			...overrides,
		});

		if (!this.dependencies.isInputCurrent()) {
			return createResult('stale');
		}

		try {
			const checkpoints = this.createCheckpoints(input, evaluationCommands);
			let commandCursor = 0;

			for (const checkpoint of checkpoints) {
				const segmentCommands = evaluationCommands
					.slice(commandCursor, checkpoint.commandIndex)
					.map((command) => this.dependencies.rewriteCommand(
						structuredClone(command),
					));
				if (segmentCommands.length > 0 || !hasExecutedSegment) {
					const segmentResult = await this.executeSegment({
						commands: segmentCommands,
						fullCommands,
						clearFirst: !hasExecutedSegment,
						previousRevision: ownedWorkerRevision,
					});
					hasExecutedSegment = true;
					segmentCount += 1;
					ownedWorkerRevision = segmentResult.receipt.requestRevision;
					latestSummary = segmentResult.receipt.result.summary;
					this.recordExecutedCommands({
						commands: segmentCommands,
						succeededCommandIds: segmentResult.receipt.result.succeededCommandIds,
						activeCommands,
						nextFullCommandById,
					});
					if (segmentResult.stale) {
						return createResult('stale');
					}
					if (segmentResult.receipt.result.status === 'stopped') {
						return createResult('stopped', {
							failedFeatureId: segmentResult.receipt.result.failedCommandId,
							error: segmentResult.receipt.result.error,
						});
					}
				}
				commandCursor = checkpoint.commandIndex;

				let owners: WorkerTopologyCheckpointOwner[] = [];
				if (checkpoint.owners.length > 0) {
					const checkpointResult = await this.dependencies.queryTopologyCheckpoint({
						owners: checkpoint.owners.map((owner) => structuredClone(owner)),
						replayVisibilityCommands: fullCommands,
						...(checkpoint.includeConvertEntitiesFaceBoundaries
							? {includeConvertEntitiesFaceBoundaries: true}
							: {}),
					});
					checkpointQueryCount += 1;
					if (
						!this.isSameWorkerRevision(
							ownedWorkerRevision,
							this.dependencies.readWorkerRevision(),
						)
						|| !this.dependencies.isInputCurrent()
					) {
						return createResult('stale');
					}
					owners = checkpointResult.owners;
				}

				/**
				 * checkpoint 的业务输入与 Worker topology 必须拥有同一个提交权。
				 * 该 guard 会继续传到引用 Repository transaction 和目标草图最终 mutation，
				 * 防止查询返回后才发生的 Worker mutation 把旧 topology 写入正式状态。
				 */
				const isCheckpointInputCurrent = (): boolean => (
					this.dependencies.isInputCurrent()
					&& this.isSameWorkerRevision(
						ownedWorkerRevision,
						this.dependencies.readWorkerRevision(),
					)
				);
				const checkpointRebuildResult = await this.dependencies.rebuildCheckpoint({
					referenceSnapshot: input.referenceSnapshot,
					targetSketchIds: checkpoint.targetSketchIds,
					owners,
					propagationId: input.propagationId,
					isInputCurrent: isCheckpointInputCurrent,
				});
				if (!isCheckpointInputCurrent()) {
					return createResult('stale');
				}
				checkpoint.targetSketchIds.forEach((sketchId) => (
					changedSketchIds.add(sketchId)
				));
				[
					...checkpointRebuildResult.keptReferenceIds,
					...checkpointRebuildResult.removedReferenceIds,
					...checkpointRebuildResult.failedReferenceIds,
				].forEach((referenceId) => changedReferenceIds.add(referenceId));
				checkpointRebuildResult.diagnostics
					.filter((diagnostic) => diagnostic.level === 'warning')
					.forEach((diagnostic) => warnings.push(diagnostic));
			}

			const remainingCommands = evaluationCommands
				.slice(commandCursor)
				.map((command) => this.dependencies.rewriteCommand(
					structuredClone(command),
				));
			if (remainingCommands.length > 0 || !hasExecutedSegment) {
				const segmentResult = await this.executeSegment({
					commands: remainingCommands,
					fullCommands,
					clearFirst: !hasExecutedSegment,
					previousRevision: ownedWorkerRevision,
				});
				segmentCount += 1;
				latestSummary = segmentResult.receipt.result.summary;
				this.recordExecutedCommands({
					commands: remainingCommands,
					succeededCommandIds: segmentResult.receipt.result.succeededCommandIds,
					activeCommands,
					nextFullCommandById,
				});
				if (segmentResult.stale) {
					return createResult('stale');
				}
				if (segmentResult.receipt.result.status === 'stopped') {
					return createResult('stopped', {
						failedFeatureId: segmentResult.receipt.result.failedCommandId,
						error: segmentResult.receipt.result.error,
					});
				}
			}

			return createResult('success');
		} catch (error) {
			if (isStaleWorkerResponseError(error)) {
				throw error;
			}
			return createResult('infrastructure-failure', {
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	/**
	 * 创建本轮不可变 checkpoint 计划。
	 *
	 * 每个目标草图只按真实时间线位置出现一次；相同位置合并，topology owner 去重。
	 *
	 * @param input 已冻结执行输入。
	 * @param evaluationCommands 当前正式 Rollback Bar 上方命令。
	 * @returns 按命令位置升序排列的 checkpoint。
	 */
	private createCheckpoints(
		input: FeatureTimelineRebuildInput,
		evaluationCommands: readonly CadCommand[],
	): Array<{
		commandIndex: number;
		targetSketchIds: string[];
		owners: TopologySourceOwnerDescriptor[];
		includeConvertEntitiesFaceBoundaries: boolean;
	}> {
		const checkpointsByCommandIndex = new Map<number, {
			targetSketchIds: Set<string>;
			owners: Map<string, TopologySourceOwnerDescriptor>;
			includeConvertEntitiesFaceBoundaries: boolean;
		}>();
		const sketchByPersistentId = new Map(
			input.sketches.map((sketch) => [sketch.persistentId, sketch]),
		);
		const referencesByTargetSketchId = new Map<string, TimelineReferenceSnapshotItem[]>();
		input.referenceSnapshot.items.forEach((item) => {
			const references = referencesByTargetSketchId.get(item.targetSketchId) ?? [];
			references.push(structuredClone(item));
			referencesByTargetSketchId.set(item.targetSketchId, references);
		});

		referencesByTargetSketchId.forEach((references, targetSketchId) => {
			const sketch = sketchByPersistentId.get(targetSketchId);
			if (!sketch) {
				throw new Error(`时间线引用目标草图不存在：${targetSketchId}`);
			}
			const consumerCommandIndex = evaluationCommands.findIndex((command) => (
				featureCommandAdapterRegistry
					.createPrototypeFromCommand(command)
					?.getSourceSketchId() === targetSketchId
			));
			const topLevelTimelineIndex = input.timelineItems.findIndex((item) => (
				item.kind === FeatureRollbackTimelineItemKind.Sketch
				&& (item.id === sketch.runtimeId || item.id === sketch.persistentId)
			));
			const hasActiveTopLevelSketch = topLevelTimelineIndex >= 0
				&& topLevelTimelineIndex < input.rollbackCursorIndex;
			if (consumerCommandIndex >= 0 && hasActiveTopLevelSketch) {
				throw new Error(
					`草图同时存在顶级时间线位置和消费 Feature：${targetSketchId}`,
				);
			}

			let commandIndex = consumerCommandIndex;
			if (hasActiveTopLevelSketch) {
				commandIndex = input.timelineItems
					.slice(0, topLevelTimelineIndex)
					.filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
					.length;
			}
			if (commandIndex < 0) {
				const existsBelowRollbackCursor = topLevelTimelineIndex >= input.rollbackCursorIndex
					|| input.fullCommands.some((command) => (
						featureCommandAdapterRegistry
							.createPrototypeFromCommand(command)
							?.getSourceSketchId() === targetSketchId
					));
				if (existsBelowRollbackCursor) {
					return;
				}
				throw new Error(`无法确定引用目标草图的时间线位置：${targetSketchId}`);
			}

			const checkpoint = checkpointsByCommandIndex.get(commandIndex) ?? {
				targetSketchIds: new Set<string>(),
				owners: new Map<string, TopologySourceOwnerDescriptor>(),
				includeConvertEntitiesFaceBoundaries: false,
			};
			checkpoint.targetSketchIds.add(targetSketchId);
			references.forEach((reference) => {
				if (
					reference.reference.selectionIntent?.intentKind === 'topology-face'
					|| reference.reference.selectionIntent?.intentKind === 'topology-loop'
				) {
					checkpoint.includeConvertEntitiesFaceBoundaries = true;
				}
				if (reference.sourceDescriptor.kind !== 'topology-edge') {
					return;
				}
				/*
				 * 面选择以 topologyFaceSource 作为持久权威，成员边只是动态边界成员。
				 * checkpoint 必须按面 owner 查询，否则旧成员 owner 漂移时 Worker
				 * 无法返回面所属实体血缘，后续协调也就没有正确候选域。
				 */
				const faceOwner = reference.reference.selectionIntent?.intentKind === 'topology-face'
					? reference.reference.selectionIntent.topologyFaceSource?.owner
					: undefined;
				const owner = faceOwner ?? reference.sourceDescriptor.owner;
				checkpoint.owners.set(
					resolveTopologySourceOwnerIdentityKey(owner),
					structuredClone(owner),
				);
			});
			checkpointsByCommandIndex.set(commandIndex, checkpoint);
		});

		return [...checkpointsByCommandIndex.entries()]
			.sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
			.map(([commandIndex, checkpoint]) => ({
				commandIndex,
				targetSketchIds: [...checkpoint.targetSketchIds],
				owners: [...checkpoint.owners.values()],
				includeConvertEntitiesFaceBoundaries:
					checkpoint.includeConvertEntitiesFaceBoundaries,
			}));
	}

	/**
	 * 执行一个不可分割的 Worker 命令段并校验 revision 所有权。
	 *
	 * @param input 当前命令段、完整可见性时间线与上一 revision。
	 * @returns Worker receipt 与 stale 判定。
	 */
	private async executeSegment(input: {
		commands: CadCommand[];
		fullCommands: CadCommand[];
		clearFirst: boolean;
		previousRevision: FeatureTimelineRebuildInput['initialWorkerRevision'];
	}): Promise<{
		receipt: FeaturePrefixBatchExecutionReceipt;
		stale: boolean;
	}> {
		const receipt = await this.dependencies.executeFeaturePrefixBatch({
			commands: input.commands,
			clearFirst: input.clearFirst,
			replayVisibilityCommands: input.fullCommands,
		});
		return {
			receipt,
			stale: !this.isExpectedNextRevision(
				input.previousRevision,
				receipt.requestRevision,
			)
				|| !this.isSameWorkerRevision(
					receipt.requestRevision,
					this.dependencies.readWorkerRevision(),
				)
				|| !this.dependencies.isInputCurrent(),
		};
	}

	/**
	 * 把本段成功命令写入活动前缀，并更新同 ID 的完整历史命令参数。
	 *
	 * @param input 当前段命令、成功 ID 与结果集合。
	 */
	private recordExecutedCommands(input: {
		commands: readonly CadCommand[];
		succeededCommandIds: readonly string[];
		activeCommands: CadCommand[];
		nextFullCommandById: Map<string, CadCommand>;
	}): void {
		const succeededCommandIdSet = new Set(input.succeededCommandIds);
		input.commands.forEach((command) => {
			if (!succeededCommandIdSet.has(command.id)) {
				return;
			}
			const stableCommand = structuredClone(command);
			input.activeCommands.push(stableCommand);
			input.nextFullCommandById.set(command.id, structuredClone(stableCommand));
		});
	}

	/**
	 * 校验下一次 mutation receipt 是否恰好承接前一 revision。
	 *
	 * @param previous 上一同步边界 revision。
	 * @param next 本次 Worker mutation 使用的 revision。
	 * @returns 文档未切换且 historySeq 只推进一次时返回 true。
	 */
	private isExpectedNextRevision(
		previous: FeatureTimelineRebuildInput['initialWorkerRevision'],
		next: FeatureTimelineRebuildInput['initialWorkerRevision'],
	): boolean {
		return previous.documentId === next.documentId
			&& previous.documentRevision === next.documentRevision
			&& next.historySeq === previous.historySeq + 1;
	}

	/**
	 * 校验 receipt 返回后 Worker revision 未被其他 mutation 推进。
	 *
	 * latestRequestSeq 会被只读查询推进，不参与模型状态新旧判断。
	 *
	 * @param expected 当前执行器拥有的 revision。
	 * @param current Worker 当前 revision。
	 * @returns 文档与 history epoch 一致时返回 true。
	 */
	private isSameWorkerRevision(
		expected: FeatureTimelineRebuildInput['initialWorkerRevision'],
		current: FeatureTimelineRebuildInput['initialWorkerRevision'],
	): boolean {
		return expected.documentId === current.documentId
			&& expected.documentRevision === current.documentRevision
			&& expected.historySeq === current.historySeq;
	}
}
